/**
 * Generates and displays a project invoice budget, including all cost, claim, and percentage fields for tasks and variations.
 *
 * @param {Object}   props
 * @param {boolean}  props.hasStages - Whether the project has stages.
 * @param {Array}    props.optionData - Array of variation/option data.
 *
 * Calculates totals for a project, including all tasks, subtasks, and direct materials, and manages all input state, validation, and claim logic for invoice generation.
 *
 * Use case: Used to calculate and summarize all cost, sale, and profit data for a project, including all subtasks and direct materials, and to generate a new progress claim invoice.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import idx from 'idx';
import _ from 'lodash';
import {
  warningSnackBar,
  infoSnackBar,
} from '../../../../actions/common.action';
import {
  addProgressClaim,
  getProgressClaimById,
  editProgressClaim,
} from '../../../../actions/project-management.action';
import InputAdornment from '@material-ui/core/InputAdornment';
import './budget.scss';
import { toUSD } from '../../../../util/currency-formatter';
import { PROJECT_MANAGEMENT } from '../../../../constants/route-paths';
import Button from 'react-bootstrap/Button';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import '../../scss/task.scss';
import DatePickerInput from '../../../../components/inputs/date-picker';
import TextAreaInput from '../../../../components/inputs/textarea-input';
import { ValidateField, ValidatorTypes } from '../../../../util/validators';
import StageAccordionGenerateInvoice from '../../../../components/stage-accordion/generate-invoice';
import { Card, CardBody, CardHeader } from 'react-bootstrap';
import { calculateStageAveragesAndTotal } from '../../../../components/stage-accordion/data/generate-invoice';
import { useParams, useNavigate } from 'react-router-dom';
import PreviewBudgetTable from './preview-budget-table';
import RenderRowData from './render-generate-invoice-rowdata';
import {
  combineProjectData,
  getProjectData,
} from './utils/preview-budget/getProjectData';
import { calculateTotal, progressClaim } from './utils/preview-budget/calculateTotal';
import {
  updateClaimAmountChange,
  updatePercentageChange,
  updateRemainingClaimAmountChange,
} from './utils/preview-budget/changeHandler';
import ProgressClaimRetention from '../../../../components/progressclaim-retention';
import { sumRetentionAmount } from './utils/preview-budget/sumRetentionAmount';
import { stageTotal } from '../../../../util/calculations/calculate-stage';
import { optionsTotal } from '../../../../util/calculations/calculate-option';
import { combineData, getVariationNumber } from '../../../../util/common';
import { filterLockedSections, extractExistingTaskIds } from '../../../../util/filter-locked-sections';

const CreateBudget = ({ hasStages, optionData, ...props }) => {
  // --- Hooks and router setup ---
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId: paramsProjectId, progressClaimId } = useParams();

  // Function to show info snackbar
  const showInfoSnackBar = (message) => {
    dispatch(infoSnackBar(message));
  };

  // --- Main state for all form fields, data, and UI ---
  const [state, setState] = useState({
    open2: false,
    open3: false,
    tasks: [],
    variations: [],
    description: {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      touched: false,
      error: false,
      valid: true,
      value: '',
      open: {},
      isHTClassRequired: false,
      validation: [ValidatorTypes.REQUIRED, ValidatorTypes.TEXTAREA],
    },
    retentionClaimAmount: {
      name: 'retentionInput',
      label: '',
      type: 'number',
      touched: true,
      error: false,
      valid: true,
      value: '',
      inputProps: {
        startAdornment: <InputAdornment position='start'>$</InputAdornment>,
      },
    },
    retentionPCAmount: {
      name: 'retentionPCAmount',
      label: '',
      type: 'number',
      touched: true,
      error: false,
      valid: true,
      value: '',
      inputProps: {
        startAdornment: <InputAdornment position='start'>$</InputAdornment>,
      },
    },
    isManualOverride: false,
    invoiceDate: new Date(),
    dueDate: new Date(),
    projectId: '',
    projectName: '',
    projectDescription: '',
    customerName: '',
    hasStages: false,
    projectType: '',
    projectQuoteType: '',
    customerLevel: '',
    levelId: '',
    projectStages: [],
    projectDataType: '',
    stages: [],
    originalStages: [],  // Unfiltered stages for calculating summaries
    optionData: [],
    progressClaims: [],
    selectedTask: [],
    stageSummaries: {},
    totalCost: 0,
    totalClaimedAmount: 0,
    totalPercentageComplete: 0,
    overallClaimPercentage: 0,
    stillToClaimPercentage: 0,
    retentionAmount: 0,
    retentionPercentage: 0,
    autoCalculateRetention: false,
    maxRetentionPercentage: 5,
    defaultRetentionPercentage: 10,
    claimAmount: 0,
    paramsProjectId: '',
    progressClaimId: '',
    claimDisplayId: '',
    hasRetentionErrors: false,
  });

  // --- Effect to recalculate stage averages when selectedTask changes ---
  useEffect(() => {
    if (state.selectedTask && state.selectedTask.length > 0 && state.stages) {
      calculateStageAverages(state.stages);
    }
  }, [state.selectedTask, state.stages]);

  // --- Initial data load on mount ---
  useEffect(() => {
    initialize();
  }, []);

  // --- Build map of section ID -> descendant task IDs for aggregation ---
  const childTaskMap = useMemo(() => {
    const map = {};
    function processStage(stage) {
      let ids = [];
      if (stage.tasks && Array.isArray(stage.tasks)) ids.push(...stage.tasks.map(t => t.id));
      if (stage.stages && Array.isArray(stage.stages)) {
        stage.stages.forEach(nested => {
          ids.push(...processStage(nested));
        });
      }
      map[stage.id] = ids; // Store all descendants for this stage
      return ids;
    }
    if (state.stages && Array.isArray(state.stages)) {
      state.stages.forEach(s => processStage(s));
    }
    return map;
  }, [state.stages]);

  // --- Recalculate totals when progress claims change ---
  useEffect(() => {
    if (!_.isEmpty(state.progressClaims)) {
      const total = calculateTotal({
        selectedTask: state.selectedTask,
        progressClaims: state.progressClaims,
        progressClaimId,
        isRemainingAmountChange: true, // Preserve user-entered amounts
        childTaskMap,
      });
      setState((prev) => ({ ...prev, ...total }));
    }
  }, [state.progressClaims, childTaskMap]);

  const projectTotal = useMemo(() => {
    // Only recalculate when these dependencies change
    const { stages, optionData: optionDataState, projectType } = state;

    try {
      // 1. Get options/variations data (same as invoices-list.jsx)
      const optionData = getVariationNumber(optionDataState || []);
      const optionCalculation = optionsTotal({ options: optionData, projectType: projectType || 'quote' });

      // 2. Process stages data (same as invoices-list.jsx)
      const sectionData = combineData({ stages: stages || [], variation: optionData }) || [];

      // 3. Calculate stage totals (same as invoices-list.jsx)
      const stageData = stageTotal(sectionData);

      // 4. Calculate revised project total (same as invoices-list.jsx)
      const revisedProjectTotal = stageData.projectTotal.totalSaleAmount + optionCalculation.projectTotal.totalSaleAmount;

      return revisedProjectTotal;
    } catch (error) {
      // Fallback to simple calculation if utilities fail
      const fallbackTotal = (state.selectedTask || []).reduce((sum, task) => {
        return sum + (parseFloat(task.materialTotal_SaleAmount || 0) +
          parseFloat(task.laborTotal_SaleAmount || 0) +
          parseFloat(task.subcontractorTotal_SaleAmount || 0));
      }, 0);
      return fallbackTotal;
    }
  }, [state.stages, state.optionData, state.projectType, state.selectedTask]); // Only recalculate when these change

  /**
   * Loads all project/task/claim data and initializes state for editing or creating a claim.
   */
  const initialize = async () => {
    let open = {};
    let description = { ...state.description };
    let retentionClaimAmount = { ...state.retentionClaimAmount };
    let retentionPCAmount = { ...state.retentionPCAmount };
    let invoiceDate = state.invoiceDate;
    let dueDate = state.dueDate;
    let selectedTask = [];
    let projectId = paramsProjectId;
    let progressClaims = [];
    let stagesData = props.stages || [];
    let hasStagesData = hasStages || false;
    let optionDataLocal = optionData || [];
    let project = {};
    let projectQuoteType = '';
    let isManualOverrideLocal = false
    let claimDisplayIdLocal = 'Not Generated';

    if (paramsProjectId) {
      const projectData = await getProjectData({
        projectId: paramsProjectId,
        progressClaimId,
        setProjectDataAndOptionData,
        dispatch,
      });

      projectId = projectData.projectId;
      selectedTask = projectData.selectedTask;
      progressClaims = projectData.progressClaims;
      stagesData = projectData.stages;
      hasStagesData = projectData.hasStages;
      optionDataLocal = projectData.optionData;
      project = projectData.projectData;
      projectQuoteType = projectData.projectQuoteType;
      dueDate = projectData.dueDate;
    }

    if (Array.isArray(selectedTask)) {
      selectedTask.forEach((task) => {
        open[task.id] = false;
      });
    }

    if (progressClaimId) {
      const query = `?id=${progressClaimId}`;
      const progressClaimDetails = await dispatch(getProgressClaimById(query));
      if (progressClaimDetails) {
        description.value = progressClaimDetails.description || '';
        retentionClaimAmount.value = progressClaimDetails.retentionClaimAmount || 0;
        retentionPCAmount.value = progressClaimDetails.retentionAmount || 0;
        isManualOverrideLocal = progressClaimDetails.isManualOverride || false;
        claimDisplayIdLocal = progressClaimDetails?.id || 'Not Generated';
        invoiceDate = progressClaimDetails.invoiceDate
          ? new Date(progressClaimDetails.invoiceDate)
          : new Date();
        dueDate = progressClaimDetails.dueDate
          ? new Date(progressClaimDetails.dueDate)
          : new Date();
      }

      // Build a map of taskId -> attributedStageId to handle draft aggregation
      const taskAttributionMap = new Map();
      const buildAttributionMap = (stageList, currentAttributedId = null) => {
        if (!stageList) return;
        stageList.forEach(stage => {
          // Determine if this stage sets a new attribution context
          let nextAttributedId = currentAttributedId;
          if (stage.is_attribution_level === true || stage.lock_flag === true) {
            nextAttributedId = stage.id;
          }

          // Map all tasks in this stage to the current attributed ID
          if (Array.isArray(stage.tasks)) {
            stage.tasks.forEach(task => {
              if (nextAttributedId) {
                taskAttributionMap.set(task.id, nextAttributedId);
              }
            });
          }

          // Recurse
          if (Array.isArray(stage.stages)) {
            buildAttributionMap(stage.stages, nextAttributedId);
          }
        });
      };
      buildAttributionMap(stagesData);

      if (
        progressClaimDetails &&
        Array.isArray(progressClaimDetails.progressClaimTask)
      ) {
        progressClaimDetails.progressClaimTask.forEach((claim) => {
          if (Array.isArray(selectedTask)) {
            // Check if this is a section-level claim (has stageId, no taskId)
            if (claim.type === 'SECTION' && claim.stageId) {
              // Find the section task by stageId
              const sectionTask = selectedTask.find(
                (t) => t.id === claim.stageId && t.type === 'SECTION'
              );
              if (sectionTask) {
                // Populate section claim data
                sectionTask.remainingPercentage = parseFloat(claim.percentageComplete || claim.completion_percentage || 0);
                sectionTask.remainingAmount = parseFloat(claim.total || 0);
                sectionTask.previousPercentageComplete = parseFloat(claim.previous_percentageComplete || 0);
                sectionTask.previousClaimAmount = parseFloat(claim.previous_total || 0);
                sectionTask.percentageComplete =
                  parseFloat(sectionTask.previousPercentageComplete || 0) +
                  parseFloat(sectionTask.remainingPercentage || 0);
                sectionTask.claimRemainingAmount = claim.claimRemainingAmount;
                // Mark as selected if there's a claim amount
                if (sectionTask.remainingAmount > 0 || sectionTask.remainingPercentage > 0) {
                  sectionTask.isSelected = true;
                }
              }
            } else {
              // Regular task or variation claim
              const task = selectedTask.find((task) => task.id === claim.taskId);
              if (task) {
                // Direct match (Task or Variation)
                task.remainingPercentage = claim.completion_percentage;
                task.remainingAmount = claim.total;
                task.previousPercentageComplete = claim.previous_completion_percentage; // progressClaim(task, progressClaims, progressClaimId);
                task.previousClaimAmount = claim.previous_total; // progressClaim(task, progressClaims, progressClaimId);
                task.percentageComplete =
                  parseFloat(task.previousPercentageComplete) +
                  parseFloat(task.remainingPercentage);
                task.claimRemainingAmount = claim.claimRemainingAmount;
                // Mark as selected if there's a claim amount
                if (task.remainingAmount > 0 || task.remainingPercentage > 0) {
                  task.isSelected = true;
                }
              } else {
                // No direct match? Check if this task belongs to an attributed section claim
                const attributedStageId = taskAttributionMap.get(claim.taskId);
                if (attributedStageId) {
                  const sectionTask = selectedTask.find(t => t.id === attributedStageId && t.type === 'SECTION');
                  if (sectionTask) {
                    // Aggregate draft claim amount into the section total
                    // Note: percentage will be recalculated based on total amount
                    sectionTask.remainingAmount = (parseFloat(sectionTask.remainingAmount) || 0) + parseFloat(claim.total || 0);

                    // For sections, we recalculate the percentage based on the aggregated total
                    if (sectionTask.totalBefore > 0) {
                      sectionTask.remainingPercentage = (sectionTask.remainingAmount / sectionTask.totalBefore) * 100;
                    }

                    // Also set selected state if any child has claim
                    if (claim.total > 0) {
                      // We don't set isSelected here because the loop below handles it based on remainingAmount/Percentage > 0
                    }
                  }
                }
              }
            }
          }
        });
      }
    } else {
      dueDate = new Date();
      invoiceDate = new Date();
    }

    const retentionAmount = await sumRetentionAmount({
      projectId: paramsProjectId,
      progressClaimId,
      dispatch,
    });

    const total = calculateTotal({
      selectedTask,
      progressClaims,
      progressClaimId,
    });

    // Apply lock_flag filtering to stages for invoice task selection
    // When a section has lock_flag=true, hide child Tasks/Sections from selection
    // Historical invoices (draft invoices with child Task assignments) remain accessible
    // 
    // IMPORTANT: We store BOTH original and filtered stages:
    // - originalStages: Used for calculating stage summaries (so locked sections show totals)
    // - stages (filtered): Used for display/accordion rendering (hides children of locked sections)
    let filteredStages = stagesData;
    let existingTaskIds = [];

    if (progressClaimId) {
      // Edit mode - extract existing task IDs to preserve historical data
      const existingClaim = progressClaims.find(c => c.u_id === progressClaimId) || {};
      existingTaskIds = extractExistingTaskIds(existingClaim);

      // For historical invoices, preserve task visibility even if section is now locked
      filteredStages = filterLockedSections(stagesData, {
        alwaysIncludeTaskIds: existingTaskIds
      });
    } else {
      // Create mode - apply full lock_flag filtering, hiding children of locked sections
      filteredStages = filterLockedSections(stagesData);
    }

    setState((prev) => {
      return {
        ...prev,
        ...project,
        projectId,
        progressClaims,
        open,
        stages: filteredStages,           // Filtered stages for display
        originalStages: stagesData,       // Original stages for calculating summaries
        optionData: optionDataLocal,
        hasStages: hasStagesData,
        paramsProjectId,
        progressClaimId,
        claimDisplayId: claimDisplayIdLocal,
        description,
        retentionClaimAmount,
        retentionPCAmount,
        isManualOverride: progressClaimId ? isManualOverrideLocal : false,
        autoCalculateRetention: project.autoCalculateRetention,
        maxRetentionPercentage: project.maxRetentionPercentage,
        defaultRetentionPercentage: project.defaultRetentionPercentage,
        invoiceDate,
        dueDate,
        selectedTask,
        projectQuoteType,
        ...retentionAmount,
        ...total,
      };
    });
  };

  /**
   * Calculates and updates stage-level averages and totals for display.
   * 
   * Uses originalStages (unfiltered) to calculate summaries, 
   * ensuring locked sections still show their rolled-up totals from child tasks.
   */
  const calculateStageAverages = (stagesData) => {
    // Use original (unfiltered) stages to calculate summaries
    // This ensures locked sections still show totals from their hidden child tasks
    const dataForCalculation = state.originalStages || stagesData;

    const sectionHeader = combineProjectData({
      stagesData: dataForCalculation,
      optionData: state.optionData,
    });
    const stageSummaries = calculateStageAveragesAndTotal(sectionHeader, {
      progressClaims: state.progressClaims,
      progressClaimId: progressClaimId,
      selectedTask: state.selectedTask
    });
    setState((prev) => {
      return { ...prev, stageSummaries };
    });
  };

  /**
   * Sets project and option data in state after loading.
   */
  const setProjectDataAndOptionData = async (projectData, optionData) => {
    setState((prev) => ({ ...prev, projectData, optionData }));
  };

  /**
   * Updates a single task in all nested stages by id.
   * Also handles updating stage properties for section-level claims.
   */
  const updateTaskInStages = ({ stages, taskId, taskIndex, selectedTask }) => {
    stages.forEach((stage) => {
      // Check if this is a section-level claim (taskId matches stage.id)
      if (stage.id === taskId) {
        const updated = selectedTask.find((t) => t.id === taskId);
        if (updated) {
          // Update relevant claim properties on the stage object itself
          stage.remainingPercentage = updated.remainingPercentage;
          stage.remainingAmount = updated.remainingAmount;
          stage.previousPercentageComplete = updated.previousPercentageComplete;
          stage.previousClaimAmount = updated.previousClaimAmount;
          stage.percentageComplete = updated.percentageComplete;
          stage.claimRemainingAmount = updated.claimRemainingAmount;
          // Ensure total totals are synced if needed
          stage.totalBefore = updated.totalBefore;
        }
      }

      if (Array.isArray(stage.stages))
        updateTaskInStages({
          stages: stage.stages,
          taskId,
          taskIndex,
          selectedTask,
        });
      if (Array.isArray(stage.tasks)) {
        const idx = stage.tasks.findIndex((t) => t.id === taskId);
        if (idx !== -1) {
          const updated = selectedTask.find((t) => t.id === taskId);
          if (updated) stage.tasks[idx] = { ...updated };
        }
      }
    });
  };

  /**
   * Handler for completed/remaining percentage changes in a task row.
   */
  const handlePercentageChange = (e, id) => {
    let { selectedTask, stages, optionData, progressClaims, progressClaimId } = state;
    const updatedSelectedTask = updatePercentageChange({
      event: e,
      taskId: id,
      selectedTask,
    });

    if (_.isEmpty(updatedSelectedTask)) return;

    const index = updatedSelectedTask.index;
    selectedTask = updatedSelectedTask.selectedTask;

    updateTaskInStages({ stages, taskId: id, taskIndex: index, selectedTask });

    const total = calculateTotal({
      selectedTask,
      progressClaims,
      progressClaimId,
      childTaskMap,
    });
    setState((prev) => {
      return {
        ...prev,
        selectedTask,
        stages,
        optionData,
        ...total,
      };
    });
    calculateStageAverages(stages);
  };

  /**
   * Handler for claim amount changes in a task row.
   */
  const handleClaimAmountChange = (e, id) => {
    let { selectedTask, stages, optionData, progressClaims, progressClaimId } = state;
    const updatedSelectedTask = updateClaimAmountChange({
      event: e,
      taskId: id,
      selectedTask,
      progressClaimId,
      progressClaims
    });

    if (_.isEmpty(updatedSelectedTask)) return;

    const index = updatedSelectedTask.index;
    selectedTask = updatedSelectedTask.selectedTask;

    updateTaskInStages({ stages, taskId: id, taskIndex: index, selectedTask });

    const total = calculateTotal({
      selectedTask,
      progressClaims,
      progressClaimId,
      isRemainingAmountChange: true,
      childTaskMap,
    });
    setState((prev) => {
      return {
        ...prev,
        selectedTask,
        stages,
        optionData,
        ...total,
      };
    });
    calculateStageAverages(stages);
  };

  /**
   * Handler for claim remaining checkbox changes in a task row.
   */
  const handleRemainingClaimAmountChange = (e, id) => {
    let { selectedTask, stages, optionData, progressClaims, progressClaimId } = state;
    const updatedSelectedTask = updateRemainingClaimAmountChange({
      event: e,
      taskId: id,
      selectedTask,
      progressClaims,
      progressClaimId,
      childTaskMap,
    });

    if (_.isEmpty(updatedSelectedTask)) return;

    const index = updatedSelectedTask.index;
    selectedTask = updatedSelectedTask.selectedTask;

    updateTaskInStages({ stages, taskId: id, taskIndex: index, selectedTask });

    const total = calculateTotal({
      selectedTask,
      progressClaims,
      progressClaimId,
      childTaskMap,
    });
    setState((prev) => {
      return {
        ...prev,
        selectedTask,
        stages,
        optionData,
        ...total,
      };
    });
    calculateStageAverages(stages);
  };

  /**
   * Submits the progress claim to the backend (add or edit).
   */
  const generateProgressClaim = async () => {
    //eas
    const {
      selectedTask = [],
      description,
      progressClaimTotal,
      progressClaimId,
      invoiceDate,
      dueDate,
      projectId: projId,
      claimAmount: currentClaimAmount,
      retentionOverride
    } = state;

    const { error, valid } = ValidateField({
      value: description.value,
      validation: description.validation,
    });
    if (!valid) {
      setState((prev) => ({
        ...prev,
        description: { ...description, error, valid },
      }));
      dispatch(warningSnackBar('Description is required!'));
      return;
    }
    if (!selectedTask.length) {
      dispatch(warningSnackBar('No Task!'));
      return;
    }

    const filteredSelectedTask = selectedTask.filter((task) => task.isSelected);
    const hasRetentionRelease = (retentionOverride?.add > 0);
    if (!filteredSelectedTask.length && !hasRetentionRelease) {
      dispatch(warningSnackBar('No Task Claimed!'));
      return;
    }

    try {
      if (progressClaimId) {
        await dispatch(
          editProgressClaim(
            {
              tasks: filteredSelectedTask,
              projectId: projId,
              description: description.value,
              total: progressClaimTotal,
              claimAmount: currentClaimAmount,
              invoiceDate: invoiceDate ? invoiceDate : null,
              dueDate: dueDate ? dueDate : null,
              // Pass overrides from RetentionBlock when unlocked
              ...(retentionOverride ? {
                isManualOverride: retentionOverride.isManualOverride,
                lessRetention: retentionOverride.less,
                addRetention: retentionOverride.add
              } : {})
            },
            progressClaimId
          )
        ).catch((e) => console.log(e));
      } else {
        await dispatch(
          addProgressClaim({
            tasks: filteredSelectedTask,
            projectId: projId,
            description: description.value,
            claimAmount: currentClaimAmount,
            total: progressClaimTotal,
            invoiceDate: invoiceDate ? invoiceDate : null,
            dueDate: dueDate ? dueDate : null,
            ...(retentionOverride ? {
              isManualOverride: retentionOverride.isManualOverride,
              lessRetention: retentionOverride.less,
              addRetention: retentionOverride.add
            } : {})
          })
        );
      }
      cancelHandler();
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * Handler for date picker changes.
   */
  const inputChangeDateHandler = (date, name) => {
    setState((prev) => ({
      ...prev,
      [name]: date,
    }));
  };

  /**
   * Handler for description textarea changes.
   */
  const descriptionChangeHandler = (e) => {
    const { description } = state;
    const { error, valid } = ValidateField({
      value: e.target.value,
      validation: description.validation,
    });
    setState((prev) => ({
      ...prev,
      description: { ...description, value: e.target.value, error, valid },
    }));
  };

  /**
   * Handler for expanding/collapsing a task row.
   */
  const handleExpandIcon = (task, open) => {
    setState((prev) => ({
      ...prev,
      open: { ...open, [task.id]: !prev.open[task.id] },
    }));
  };

  // --- Navigation handlers for breadcrumbs ---
  const ViewInvoice = () => {
    navigate(`/${PROJECT_MANAGEMENT}/invoices/${state.paramsProjectId}`);
  };

  const ViewProjectList = () => {
    navigate(`/${PROJECT_MANAGEMENT}?project-type=${state.projectDataType}`);
  };

  const ViewProject = () => {
    navigate(`/${PROJECT_MANAGEMENT}/view/${state.projectId}`);
  };

  /**
   * Cancels and navigates back to the invoice/project list.
   */
  const cancelHandler = () => {
    if (paramsProjectId) {
      navigate(`/${PROJECT_MANAGEMENT}/invoices/${paramsProjectId}`);
      return;
    }
    props.handleBudgetClick && props.handleBudgetClick();
  };

  // --- Destructure all state fields for rendering ---
  const {
    projectQuoteType,
    selectedTask,
    description,
    progressClaims,
    stages,
    optionData: optionDataState,
    claimAmount,
    claimsRetainedTotal,
    claimsRetentionClaimTotal,
    previous_total_claim_amount,
    invoiceDate,
    dueDate,
    projectName,
    stageSummaries = {},
  } = state;

  // --- Main render: invoice/budget UI ---
  return (
    <div className='purchase-order-block'>
      <div className='budget-container'>
        <div className='row'>
          <div className='w-auto'>
            <h2 className='content-heading text-uppercase'>Generate Invoice</h2>
            <div className='col-12 breadcrumb-block p-0'>
              <Breadcrumb>
                <Breadcrumb.Item onClick={ViewProjectList}>
                  PROJECTS
                </Breadcrumb.Item>
                <Breadcrumb.Item onClick={ViewProject}>
                  {projectName}
                </Breadcrumb.Item>
                <Breadcrumb.Item onClick={ViewInvoice}>
                  Invoices
                </Breadcrumb.Item>
                <Breadcrumb.Item active>Generate Invoice</Breadcrumb.Item>
              </Breadcrumb>
            </div>
          </div>
        </div>

        <div className='row'>
          <div className='col-12'>
            <div className='mt-3'>
              <TextAreaInput
                {...description}
                onChange={(e) => descriptionChangeHandler(e)}
              />
            </div>
          </div>
        </div>

        <div className='row'>
          <div className='col-lg-3 col-md-6 mt-3'>
            <DatePickerInput
              label={'Invoice Date'}
              value={invoiceDate}
              onChange={(e) => inputChangeDateHandler(e, 'invoiceDate')}
            />
          </div>
          <div className='col-lg-3 col-md-6 mt-3'>
            <DatePickerInput
              label={'Due Date'}
              value={dueDate}
              onChange={(e) => inputChangeDateHandler(e, 'dueDate')}
            />
          </div>
          {projectQuoteType === 'quote' && (
            <>
              <div className='col-lg-3 col-md-6 mt-3'>
                <div className='view-block'>
                  <label>Amount Retained</label>
                  <p className='mt-2' data-cy='retention-amount-input'>{toUSD(claimsRetainedTotal || 0)}</p>
                </div>
              </div>
              <div className='col-lg-3 col-md-6 mt-3'>
                <div className='view-block'>
                  <label>Claimed Amount</label>
                  <p className='mt-2' data-cy='retention-claim-input'>{toUSD(claimsRetentionClaimTotal || 0)}</p>
                </div>
              </div>
            </>
          )}
        </div>

        <div className='row'>
          <Card className='border-0'>
            <CardHeader className='sections d-flex justify-content-between align-items-center bg-body card-header'>
              <h6 className='mb-0'>SECTIONS</h6>
            </CardHeader>
            <CardBody className='d-flex flex-column flex-wrap gap-2 p-0 pt-2'>
              {stages && Array.isArray(stages) && stages.length ? (
                stages.map((stage) => (
                  <StageAccordionGenerateInvoice
                    key={stage.id}
                    stage={stage}
                    optionData={optionDataState}
                    selectedTask={selectedTask}
                    renderRowData={(task) => (
                      <RenderRowData
                        task={task}
                        progressClaims={progressClaims}
                        open={state.open}
                        selectedTask={selectedTask}
                        handleExpandIcon={handleExpandIcon}
                        handlePercentageChange={handlePercentageChange}
                        handleClaimAmountChange={handleClaimAmountChange}
                        handleRemainingClaimAmountChange={
                          handleRemainingClaimAmountChange
                        }
                        progressClaimId={state.progressClaimId}
                        infoSnackBar={showInfoSnackBar}
                      />
                    )}
                    renderSectionRowData={(stage) => {
                      const getAllTaskIds = (s) => {
                        let ids = [];
                        if (s.tasks && Array.isArray(s.tasks)) ids.push(...s.tasks.map(t => t.id));
                        if (s.stages && Array.isArray(s.stages)) {
                          s.stages.forEach(nested => ids.push(...getAllTaskIds(nested)));
                        }
                        return ids;
                      };
                      const sectionTask = selectedTask.find(
                        (t) => (t.id === stage.id || t.stageId === stage.id) && t.type === 'SECTION'
                      );

                      if (sectionTask) {
                        // Ensure section task has the aggregated cost from summaries
                        const { totalCost } = idx(stageSummaries[stage.id], _ => _) || {};
                        sectionTask.totalBefore = totalCost || 0;

                        return (
                          <RenderRowData
                            task={sectionTask}
                            progressClaims={progressClaims}
                            open={state.open}
                            selectedTask={selectedTask}
                            handleExpandIcon={handleExpandIcon}
                            handlePercentageChange={handlePercentageChange}
                            handleClaimAmountChange={handleClaimAmountChange}
                            handleRemainingClaimAmountChange={
                              handleRemainingClaimAmountChange
                            }
                            progressClaimId={state.progressClaimId}
                            infoSnackBar={showInfoSnackBar}
                            childTaskIds={getAllTaskIds(stage)}
                          />
                        );
                      }
                      return null;
                    }}
                    stageSummaries={stageSummaries}
                    // Pass progressClaims and progressClaimId for section-level claims
                    progressClaims={progressClaims}
                    progressClaimId={state.progressClaimId}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={7} className='text-center'>
                    No Data to Show
                  </td>
                </tr>
              )}
            </CardBody>
          </Card>

          {optionDataState &&
            Array.isArray(optionDataState) &&
            optionDataState.length ? (
            <Card className='border-0'>
              <CardHeader className='sections d-flex justify-content-between align-items-center bg-body card-header mt-3'>
                <h6 className='mb-0'>VARIATIONS</h6>
              </CardHeader>
              <CardBody className='d-flex flex-column flex-wrap gap-2 p-0 pt-2'>
                <PreviewBudgetTable
                  header='Variations'
                  tbodyData={optionDataState}
                  renderRowData={(task) => (
                    <RenderRowData
                      task={task}
                      progressClaims={progressClaims}
                      open={state.open}
                      selectedTask={selectedTask}
                      handleExpandIcon={handleExpandIcon}
                      handlePercentageChange={handlePercentageChange}
                      handleClaimAmountChange={handleClaimAmountChange}
                      handleRemainingClaimAmountChange={
                        handleRemainingClaimAmountChange
                      }
                      progressClaimId={state.progressClaimId}
                      infoSnackBar={showInfoSnackBar}
                    />
                  )}
                />
              </CardBody>
            </Card>
          ) : null}

          {/* {projectQuoteType === 'quote' && ( */}
          <ProgressClaimRetention
            headerName={'Retentions'}
            projectQuoteType={projectQuoteType}
            progressClaims={progressClaims}
            progressClaimId={progressClaimId}
            projectTotal={projectTotal}
            autoCalculateRetention={state.autoCalculateRetention}
            maxRetentionPercentage={state.maxRetentionPercentage}
            defaultRetentionPercentage={state.defaultRetentionPercentage}
            currentMeta={{
              projectId: state.projectId,
              claimAmount: claimAmount,
              savedClaimAmount: progressClaimId ? (state.retentionPCAmount?.value || state.claimAmount || 0) : null,
              savedLess: state.retentionPCAmount?.value || 0,
              savedAdd: state.retentionClaimAmount?.value || 0,
              savedIsManualOverride: state.isManualOverride || false,
              claimNumber: state.claimDisplayId || 'Not Generated',
              invoiceDate: invoiceDate?.toLocaleDateString?.(),
              invoiceNumber: 'Not Invoiced',
              previouslyClaimed: previous_total_claim_amount || 0
            }}
            onCurrentChange={({ less, add, isManualOverride, context, isValid }) => setState(prev => ({
              ...prev,
              retentionOverride: { less, add, isManualOverride, context },
              hasRetentionErrors: !isValid
            }))}
          />
          {/* )} */}

          {/* <ProgressClaimRetention
            headerName={'Retentions'}
            projectQuoteType={projectQuoteType}
            progressClaims={progressClaims}
            progressClaimId={progressClaimId}
          /> */}
        </div>

        {/* <div className='col-12 total-table mt-3 table-responsive'>
          <table className='table-create table-variation mb-0 table table-bordered'>
            <thead>
              <tr>
                {projectQuoteType === 'quote' && (
                  <>
                    <th className='w-auto'>Total Retained By Customer</th>
                    <th>Total Retention Claimed Back</th>
                    <th>Remaining Retention to Claim</th>
                  </>
                )}
                {retentionTotal && projectQuoteType === 'quote' ? (
                  <th>Retention Claim Total</th>
                ) : null}
                <th>Total of Previous Claims</th>
                {variationsTotal ? <th>Variation Claim Total</th> : null}
                <th>This Claim Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {projectQuoteType === 'quote' && (
                  <>
                    <td>
                      <span className='tableinfo' data-cy="total-retained-by-customer">
                        {toUSD(claimsRetainedTotal || 0)}
                      </span>
                    </td>
                    <td>
                      <span className='tableinfo' data-cy="total-retention-claimed-back">
                        {toUSD(claimsRetentionClaimTotal || 0)}
                      </span>
                    </td>
                    <td>
                      <span className='tableinfo' data-cy="remaining-retention-to-claim">
                        {toUSD(
                          claimsRetainedTotal - claimsRetentionClaimTotal || 0
                        )}
                      </span>
                    </td>
                  </>
                )}
                {retentionTotal && projectQuoteType === 'quote' ? (
                  <td>
                    <span className='tableinfo' data-cy="retention-claim-total">
                      {toUSD(retentionTotal || 0)}
                    </span>
                  </td>
                ) : null}
                <td>
                  <span className='tableinfo'>
                    {toUSD(previous_total_claim_amount || 0)}
                  </span>
                </td>
                {variationsTotal ? (
                  <td>
                    <span className='tableinfo'>
                      {toUSD(variationsTotal || 0)}
                    </span>
                  </td>
                ) : null}
                <td>
                  <span className='tableinfo'>
                    {toUSD(round(claimAmount, 2))}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div> */}

        <div className='col-12 d-flex mt-3'>
          <Button
            type='submit'
            className='ms-auto me-3 secondarybtn'
            onClick={generateProgressClaim}
            data-cy="generate-invoice-button"
            disabled={state.hasRetentionErrors}
          >
            Generate
          </Button>
          <Button
            className='secondarybtn cancelbtn me-auto'
            onClick={cancelHandler}
            data-cy="cancel-button"
          >
            CANCEL
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateBudget;
